import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    AUTH_UNAUTHORIZED_EVENT,
    clearStoredAuth,
    loginRequest,
    logoutRequest,
    menuRequest,
    meRequest,
    refreshRequest,
    readStoredAuth,
    writeStoredAuth,
} from '../api/authApi';
import {
    authenticateBiometricSession,
    getBiometricAvailability,
} from '../mobile/biometricService';
import {
    clearTokenRefreshHandler,
    registerTokenRefreshHandler,
} from '../api/httpClient';
import { handleLogout } from '../services/shiftTrackingService';

const AuthContext = createContext(null);

function isUnauthorizedError(error) {
    return error?.response?.status === 401;
}

function getOfflineExpirationTime(stored) {
    if (stored?.offline_expires_at) {
        const parsed = Number(stored.offline_expires_at);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }
    if (stored?.expires_at) {
        const parsed = Date.parse(stored.expires_at);
        if (!Number.isNaN(parsed) && parsed > 0) {
            return parsed;
        }
    }
    return 0;
}

export function AuthProvider({ children }) {
    const [status, setStatus] = useState('loading');
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(null);
    const [menu, setMenu] = useState([]);
    const [isMenuLoading, setIsMenuLoading] = useState(false);
    const [isAuthRefreshing, setIsAuthRefreshing] = useState(true);
    const [biometricEnabled, setBiometricEnabled] = useState(false);
    const [biometricAvailability, setBiometricAvailability] = useState(null);
    const [isNetworkSessionExpired, setIsNetworkSessionExpired] = useState(false);
    const revalidatePromiseRef = useRef(null);

    const forceGuestSession = useCallback(async () => {
        await clearStoredAuth();
        setToken(null);
        setUser(null);
        setMenu([]);
        setIsMenuLoading(false);
        setIsAuthRefreshing(false);
        setBiometricEnabled(false);
        setIsNetworkSessionExpired(false);
        setStatus('guest');
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return undefined;
        }

        let cancelled = false;

        const onUnauthorized = () => {
            if (cancelled) {
                return;
            }

            // Marcar que se requiere reautenticación para llamadas de red,
            // pero NO destruir la sesión local ni expulsar al técnico si está trabajando offline.
            setIsNetworkSessionExpired(true);
        };

        window.addEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);

        return () => {
            cancelled = true;
            window.removeEventListener(AUTH_UNAUTHORIZED_EVENT, onUnauthorized);
        };
    }, [forceGuestSession]);

    useEffect(() => {
        const bootstrap = async () => {
            const stored = await readStoredAuth();

            if (!stored?.token) {
                setStatus('guest');
                setMenu([]);
                setIsMenuLoading(false);
                setIsAuthRefreshing(false);
                return;
            }

            const rememberMe = stored.remember_me !== false;


            const enabled = Boolean(stored.biometric_enabled);
            setBiometricEnabled(enabled);

            try {
                const availability = await getBiometricAvailability();
                setBiometricAvailability(availability);

                if (enabled) {
                    await authenticateBiometricSession();
                }
            } catch {
                setStatus('guest');
                setIsMenuLoading(false);
                setIsAuthRefreshing(false);
                return;
            }

            setIsMenuLoading(true);
            setIsAuthRefreshing(true);

            try {
                setToken(stored.token);
                setStatus('authenticated');

                const offlineExpiresAt = getOfflineExpirationTime(stored);
                const isOfflineValid = offlineExpiresAt > 0 && Date.now() < offlineExpiresAt;

                try {
                    const meData = await meRequest(stored.token);
                    setUser(meData.user);
                    setStatus('authenticated');

                    const nextOfflineExpiresAt = Date.now() + 7 * 86400000;

                    try {
                        const menuData = await menuRequest(stored.token);
                        const groups = menuData?.data?.groups || [];
                        setMenu(groups);
                        await writeStoredAuth({
                            token: stored.token,
                            user: meData.user,
                            menu: groups,
                            biometric_enabled: enabled,
                            remember_me: rememberMe,
                            expires_at: stored.expires_at || null,
                            offline_expires_at: nextOfflineExpiresAt,
                        });
                    } catch (menuError) {
                        if (isUnauthorizedError(menuError)) {
                            if (stored.user && isOfflineValid) {
                                setMenu(Array.isArray(stored.menu) ? stored.menu : []);
                                setIsNetworkSessionExpired(true);
                                return;
                            }
                            await clearStoredAuth();
                            setStatus('guest');
                            setToken(null);
                            setUser(null);
                            setMenu([]);
                            setIsNetworkSessionExpired(true);
                            return;
                        }

                        // Si falla menu por red/infra, mantenemos la sesión con el menú almacenado.
                        setMenu(Array.isArray(stored.menu) ? stored.menu : []);
                        await writeStoredAuth({
                            token: stored.token,
                            user: meData.user,
                            menu: Array.isArray(stored.menu) ? stored.menu : [],
                            biometric_enabled: enabled,
                            remember_me: rememberMe,
                            expires_at: stored.expires_at || null,
                            offline_expires_at: nextOfflineExpiresAt,
                        });
                    }
                } catch (meError) {
                    if (isUnauthorizedError(meError)) {
                        // Intentar renovación automática silenciosa si recordar sesión está activo
                        if (stored.token && rememberMe) {
                            try {
                                const refreshData = await refreshRequest(stored.token);
                                if (refreshData?.access_token) {
                                    const nextToken = refreshData.access_token;
                                    const nextUser = refreshData.user || stored.user;
                                    setToken(nextToken);
                                    setUser(nextUser);
                                    setStatus('authenticated');
                                    setIsNetworkSessionExpired(false);

                                    let groups = Array.isArray(stored.menu) ? stored.menu : [];
                                    try {
                                        const menuData = await menuRequest(nextToken);
                                        groups = menuData?.data?.groups || groups;
                                    } catch {
                                        // ignorar error secundario de menu
                                    }
                                    setMenu(groups);

                                    await writeStoredAuth({
                                        token: nextToken,
                                        user: nextUser,
                                        menu: groups,
                                        biometric_enabled: enabled,
                                        remember_me: true,
                                        expires_at: refreshData.expires_at || null,
                                        offline_expires_at: Date.now() + 7 * 86400000,
                                    });
                                    return;
                                }
                            } catch {
                                // Fallback a autorización offline si la renovación falla
                            }
                        }

                        // Autorización offline acotada: vigencia máxima de 7 días desde el último acceso verificado
                        if (stored.user && isOfflineValid) {
                            setUser(stored.user || null);
                            setMenu(Array.isArray(stored.menu) ? stored.menu : []);
                            setStatus('authenticated');
                            setIsNetworkSessionExpired(true);
                            return;
                        }

                        // Si expiró la vigencia offline acotada, pasar a guest pero conservar los pendientes en IndexedDB
                        await clearStoredAuth();
                        setStatus('guest');
                        setToken(null);
                        setUser(null);
                        setMenu([]);
                        setIsNetworkSessionExpired(true);
                        return;
                    }

                    // Error temporal de red/infraestructura o sin conexión:
                    // Respetar estrictamente la vigencia offline también sin conexión
                    if (stored.user && isOfflineValid) {
                        setUser(stored.user || null);
                        setMenu(Array.isArray(stored.menu) ? stored.menu : []);
                        setStatus('authenticated');
                        return;
                    }

                    // Si no tiene vigencia offline válida o ya expiró, bloquear acceso autenticado
                    await clearStoredAuth();
                    setStatus('guest');
                    setToken(null);
                    setUser(null);
                    setMenu([]);
                    setIsNetworkSessionExpired(true);
                }
            } finally {
                setIsMenuLoading(false);
                setIsAuthRefreshing(false);
            }
        };

        bootstrap();
    }, []);

    const login = useCallback(async (email, password, remember = false) => {
        if (typeof email !== 'string' || typeof password !== 'string' || typeof remember !== 'boolean') {
            throw new TypeError('Las credenciales tienen un formato inválido.');
        }

        const normalizedEmail = email.trim().toLowerCase();
        if (!normalizedEmail || normalizedEmail.length > 255 || !password || password.length > 1024) {
            throw new TypeError('Las credenciales tienen un formato inválido.');
        }

        const data = await loginRequest({ email: normalizedEmail, password, remember });
        const stored = await readStoredAuth();

        const nextAuth = {
            token: data.access_token,
            user: data.user,
            expires_at: data.expires_at,
            offline_expires_at: Date.now() + 7 * 86400000,
            biometric_enabled: Boolean(stored?.biometric_enabled),
            remember_me: Boolean(remember),
        };

        setIsMenuLoading(true);
        setIsAuthRefreshing(true);
        setToken(data.access_token);
        setUser(data.user);
        setStatus('authenticated');

        try {
            const menuData = await menuRequest(data.access_token);
            const groups = menuData?.data?.groups || [];

            await writeStoredAuth({ ...nextAuth, menu: groups });
            setMenu(groups);
        } catch (menuError) {
            if (isUnauthorizedError(menuError)) {
                await clearStoredAuth();
                setToken(null);
                setUser(null);
                setMenu([]);
                setStatus('guest');
                throw new Error('La sesion JWT no pudo validarse contra el backend actual. Inicia sesion nuevamente.');
            }

            // Si falla menu por red/infra en login, continuar con menú vacío.
            await writeStoredAuth({ ...nextAuth, menu: [] });
            setMenu([]);
        } finally {
            setIsMenuLoading(false);
            setIsAuthRefreshing(false);
        }

        return data;
    }, []);

    const reauthenticate = useCallback(async (password) => {
        if (!user?.email || typeof password !== 'string' || !password) {
            throw new TypeError('Contraseña requerida para reautenticar.');
        }

        const stored = await readStoredAuth();
        const rememberMe = Boolean(stored?.remember_me !== false);

        // 1. Ejecutar petición de autenticación sin mutar el estado local todavía
        const data = await loginRequest({
            email: user.email.trim().toLowerCase(),
            password,
            remember: rememberMe,
        });

        // 2. Validación estricta de identidad previa: verificar coincidencia exacta de usuario
        const authenticatedUserId = data?.user?.id !== undefined ? String(data.user.id) : null;
        const currentUserId = user?.id !== undefined ? String(user.id) : null;

        if (!authenticatedUserId || authenticatedUserId !== currentUserId) {
            throw new Error('La cuenta autenticada no coincide con la sesión local activa. Iniciá sesión con la misma cuenta.');
        }

        const authenticatedEmpresaId = data?.user?.empresa_id !== undefined ? String(data.user.empresa_id) : null;
        const currentEmpresaId = user?.empresa_id !== undefined ? String(user.empresa_id) : null;
        if (authenticatedEmpresaId !== null && currentEmpresaId !== null && authenticatedEmpresaId !== currentEmpresaId) {
            throw new Error('La empresa de la cuenta autenticada no coincide con la sesión local activa.');
        }

        // 3. Identidad verificada: ahora sí actualizar y persistir la nueva sesión de red
        const nextAuth = {
            token: data.access_token,
            user: data.user,
            expires_at: data.expires_at,
            offline_expires_at: Date.now() + 7 * 86400000,
            biometric_enabled: Boolean(stored?.biometric_enabled),
            remember_me: rememberMe,
        };

        setToken(data.access_token);
        setUser(data.user);
        setStatus('authenticated');
        setIsNetworkSessionExpired(false);

        try {
            const menuData = await menuRequest(data.access_token);
            const groups = menuData?.data?.groups || [];
            await writeStoredAuth({ ...nextAuth, menu: groups });
            setMenu(groups);
        } catch (menuError) {
            if (isUnauthorizedError(menuError)) {
                throw menuError;
            }
            await writeStoredAuth({ ...nextAuth, menu: Array.isArray(stored?.menu) ? stored.menu : [] });
        }

        return data;
    }, [user]);

    const enableBiometricLock = useCallback(async () => {
        const availability = await getBiometricAvailability();
        setBiometricAvailability(availability);

        if (!availability.isAvailable && !availability.deviceIsSecure) {
            throw new Error(
                availability.reason || 'Configurá biometría o un bloqueo de pantalla en el dispositivo.',
            );
        }

        await authenticateBiometricSession();
        const stored = await readStoredAuth();
        await writeStoredAuth({
            token: stored?.token || token,
            user: stored?.user || user,
            menu: stored?.menu || menu,
            expires_at: stored?.expires_at || null,
            offline_expires_at: stored?.offline_expires_at || null,
            biometric_enabled: true,
            remember_me: stored?.remember_me !== false,
        });
        setBiometricEnabled(true);
    }, [menu, token, user]);

    const disableBiometricLock = useCallback(async () => {
        const stored = await readStoredAuth();
        await writeStoredAuth({
            token,
            user,
            menu,
            expires_at: stored?.expires_at || null,
            offline_expires_at: stored?.offline_expires_at || null,
            biometric_enabled: false,
            remember_me: stored?.remember_me !== false,
        });
        setBiometricEnabled(false);
    }, [menu, token, user]);

    const logout = useCallback(async () => {
        try {
            await handleLogout();
        } catch {
            // Ignorar errores al finalizar jornada en logout
        }

        try {
            if (token) {
                await logoutRequest(token);
            }
        } catch {
            // Logout stateless: no necesitamos bloquear la salida por error de red.
        }

        await forceGuestSession();
    }, [forceGuestSession, token]);

    const refreshSession = useCallback(async () => {
        const stored = await readStoredAuth();
        const tokenToRefresh = token || stored?.token;
        if (!tokenToRefresh) {
            throw new Error('No hay sesión disponible para renovar.');
        }

        const refreshData = await refreshRequest(tokenToRefresh);
        if (!refreshData?.access_token) {
            throw new Error('Respuesta de renovación sin token de acceso.');
        }

        const nextToken = refreshData.access_token;
        const nextUser = refreshData.user || user || stored?.user;
        const rememberMe = stored?.remember_me !== false;

        setToken(nextToken);
        if (nextUser) {
            setUser(nextUser);
        }
        setIsNetworkSessionExpired(false);

        await writeStoredAuth({
            token: nextToken,
            user: nextUser,
            menu: Array.isArray(menu) && menu.length > 0 ? menu : (Array.isArray(stored?.menu) ? stored.menu : []),
            biometric_enabled: Boolean(stored?.biometric_enabled),
            remember_me: rememberMe,
            expires_at: refreshData.expires_at || null,
            offline_expires_at: Date.now() + 7 * 86400000,
        });

        return nextToken;
    }, [menu, token, user]);

    useEffect(() => {
        registerTokenRefreshHandler(refreshSession);
        return () => {
            clearTokenRefreshHandler();
        };
    }, [refreshSession]);

    const revalidateSession = useCallback(async () => {
        if (!token) {
            return false;
        }

        if (revalidatePromiseRef.current) {
            return revalidatePromiseRef.current;
        }

        revalidatePromiseRef.current = (async () => {
            try {
                const meData = await meRequest(token);
                setUser(meData.user);

                let groups = menu;
                try {
                    const menuData = await menuRequest(token);
                    groups = menuData?.data?.groups || [];
                    setMenu(groups);
                } catch (menuError) {
                    if (isUnauthorizedError(menuError)) {
                        throw menuError;
                    }

                    // Si menu falla por red/infra, mantener último menú en memoria.
                }

                const stored = await readStoredAuth();
                await writeStoredAuth({
                    token,
                    user: meData.user,
                    menu: groups,
                    expires_at: stored?.expires_at || null,
                    offline_expires_at: Date.now() + 7 * 86400000,
                    biometric_enabled: biometricEnabled,
                    remember_me: stored?.remember_me !== false,
                });
                return true;
            } catch (error) {
                const stored = await readStoredAuth();
                const offlineExpiresAt = getOfflineExpirationTime(stored);
                const isOfflineValid = offlineExpiresAt > 0 && Date.now() < offlineExpiresAt;

                if (isUnauthorizedError(error)) {
                    if (isOfflineValid && user) {
                        setIsNetworkSessionExpired(true);
                        return false;
                    }

                    await clearStoredAuth();
                    setToken(null);
                    setUser(null);
                    setMenu([]);
                    setStatus('guest');
                    return false;
                }

                // Si fue error de red/conexión, respetar vencimiento offline:
                if (!isOfflineValid) {
                    await clearStoredAuth();
                    setToken(null);
                    setUser(null);
                    setMenu([]);
                    setStatus('guest');
                    setIsNetworkSessionExpired(true);
                    return false;
                }

                return true;
            } finally {
                revalidatePromiseRef.current = null;
            }
        })();

        return revalidatePromiseRef.current;
    }, [biometricEnabled, menu, token]);

    const permissionsSet = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);

    const hasPermission = useCallback((permission) => {
        if (!permission) {
            return false;
        }

        return permissionsSet.has(permission);
    }, [permissionsSet]);

    const canAny = useCallback((permissions = []) => {
        if (!Array.isArray(permissions) || permissions.length === 0) {
            return false;
        }

        return permissions.some((permission) => hasPermission(permission));
    }, [hasPermission]);

    const value = useMemo(
        () => ({
            status,
            user,
            token,
            login,
            reauthenticate,
            logout,
            hasPermission,
            can: hasPermission,
            canAny,
            permissions: user?.permissions || [],
            menu,
            isMenuLoading,
            isAuthRefreshing,
            refreshSession,
            revalidateSession,
            biometricEnabled,
            biometricAvailability,
            enableBiometricLock,
            disableBiometricLock,
            isNetworkSessionExpired,
            setIsNetworkSessionExpired,
            isAuthenticated: status === 'authenticated',
        }),
        [status, user, token, menu, isMenuLoading, isAuthRefreshing, hasPermission, canAny, login, reauthenticate, logout, refreshSession, revalidateSession, biometricEnabled, biometricAvailability, enableBiometricLock, disableBiometricLock, isNetworkSessionExpired],
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error('useAuth debe usarse dentro de AuthProvider');
    }

    return context;
}
